/**
 * BottomNavView: the floating bottom navigation + FAB, the display half shared by the Mini App's
 * ClientBottomNav and the studio website's phone bar (shared-client-screens D3, rows N1 to N6).
 *
 * Five slots in mockup order (dashboard-mockup.html:1922–1965): Home / Bookings / [+] FAB / Invite /
 * Profile. Each surface hands in its four tabs (which one is current and what a press does) and the
 * FAB's availability-aware "Quick book" action; this view navigates nowhere by itself.
 */
import { BookingsIcon, HomeIcon, InviteIcon, PlusIcon, ProfileIcon } from './clientNavIcons';
import styles from './ClientBottomNav.module.css';

export type BottomNavTabKey = 'home' | 'bookings' | 'invite' | 'profile';

export interface BottomNavTab {
  key: BottomNavTabKey;
  label: string;
  current: boolean;
  onPress: () => void;
}

const ICONS: Record<BottomNavTabKey, JSX.Element> = {
  home: HomeIcon,
  bookings: BookingsIcon,
  invite: InviteIcon,
  profile: ProfileIcon,
};

export interface BottomNavViewProps {
  /** Home, Bookings, Invite, Profile: the first two sit left of the FAB, the last two right. */
  tabs: readonly BottomNavTab[];
  /** Availability-aware "Quick book" action. */
  onBook: () => void;
  /** Disable the FAB while what can be booked is still being read. */
  bookDisabled?: boolean;
}

export function BottomNavView({ tabs, onBook, bookDisabled = false }: BottomNavViewProps) {
  const renderTab = (tab: BottomNavTab) => (
    <button
      key={tab.key}
      type="button"
      className={`${styles.navItem} ${tab.current ? styles.active : ''}`}
      data-testid={`client-nav-${tab.key}`}
      aria-current={tab.current ? 'page' : undefined}
      onClick={tab.onPress}
    >
      {ICONS[tab.key]}
      <span className={styles.label}>{tab.label}</span>
    </button>
  );

  return (
    <div className={styles.navWrap}>
      <nav className={styles.bottomNav} data-testid="client-nav">
        {tabs.slice(0, 2).map(renderTab)}
        <div className={styles.fabSlot}>
          <button
            type="button"
            className={styles.fab}
            data-testid="client-nav-fab"
            aria-label="Quick book"
            disabled={bookDisabled}
            onClick={onBook}
          >
            {PlusIcon}
          </button>
        </div>
        {tabs.slice(2).map(renderTab)}
      </nav>
    </div>
  );
}
